/**
 * Contact page
 */

import React, {useState} from 'react';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {SectionHeader} from '../../components/ui/SectionHeader';
import {Card} from '../../components/ui/Card';
import {Button} from '../../components/ui/Button';
import {Input} from '../../components/ui/Input';
import {api} from '../../config/firebase';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {ref: headerRef, isVisible: headerVisible} = useScrollAnimation();
  const {ref: contentRef, isVisible: contentVisible} = useScrollAnimation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage(null);

    try {
      const response = await api.contact.submitContact({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        subject: formData.subject,
        message: formData.message,
        type: 'general',
      });

      if (response.success) {
        setSubmitStatus('success');
        setFormData({name: '', email: '', company: '', subject: '', message: ''});
        // Reset success message after 5 seconds
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setErrorMessage(response.error?.message || 'Failed to send message. Please try again.');
      }
    } catch (error: any) {
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LandingLayout>
      <Section padding="lg" background="white" className="pt-32 bg-background dark:bg-[#1E1E1E]">
        <Container className="max-w-4xl">
          <div
            ref={headerRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <SectionHeader
              title="Get in Touch"
              subtitle="We'd love to hear from you. Send us a message and we'll respond as soon as possible."
              centered
              size="lg"
            />
          </div>

          <div
            ref={contentRef as React.RefObject<HTMLDivElement>}
            className={`grid md:grid-cols-2 gap-12 transition-all duration-700 ${
              contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{transitionDelay: '200ms'}}
          >
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="font-semibold text-lg text-textPrimary dark:text-[#CCCCCC] mb-4">Contact Information</h3>
                <div className="space-y-4 text-textSecondary dark:text-[#858585]">
                  <div>
                    <p className="font-medium text-textPrimary dark:text-[#CCCCCC]">Email</p>
                    <a href="mailto:hello@voxapilot.com" className="hover:text-accent dark:hover:text-orange-500">
                      hello@voxapilot.com
                    </a>
                  </div>
                  <div>
                    <p className="font-medium text-textPrimary dark:text-[#CCCCCC]">Support</p>
                    <a href="mailto:support@voxapilot.com" className="hover:text-accent dark:hover:text-orange-500">
                      support@voxapilot.com
                    </a>
                  </div>
                  <div>
                    <p className="font-medium text-textPrimary dark:text-[#CCCCCC]">Sales</p>
                    <a href="mailto:sales@voxapilot.com" className="hover:text-accent dark:hover:text-orange-500">
                      sales@voxapilot.com
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg text-textPrimary dark:text-[#CCCCCC] mb-4">Office Hours</h3>
                <div className="text-textSecondary dark:text-[#858585] space-y-2">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                  <p>Saturday - Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="p-8">
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    Thank you for your message! We'll get back to you soon.
                  </p>
                </div>
              )}
              {submitStatus === 'error' && errorMessage && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-800 dark:text-red-200">{errorMessage}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                      Name *
                    </label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                    Company
                  </label>
                  <Input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                    Subject *
                  </label>
                  <Input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                    Message *
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border border-border dark:border-gray-700 bg-background dark:bg-[#252526] text-textPrimary dark:text-[#CCCCCC] focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-orange-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    rows={6}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </Card>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}

